import { Component, OnInit } from '@angular/core'
import { Auth } from '@angular/fire/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDocs,
  orderBy,
  query,
  QueryDocumentSnapshot,
} from '@angular/fire/firestore'
import { MatSnackBar } from '@angular/material/snack-bar'
import { ActivatedRoute } from '@angular/router'
import { map } from 'rxjs/operators'
import { FoodEntry, MedicineEntry } from 'src/domain/provision'
import { firebaseToDate } from '../service/time-util'

const FOOD_COLLECTION = 'food'
const MEDICINE_COLLECTION = 'medicine'

export interface FoodEntryItem extends FoodEntry {
  docId: string
}

export interface MedicineEntryItem extends MedicineEntry {
  docId: string
}

export interface NewFoodEvent {
  date: Date
  foodName: string
  foodPortion: number
  dogWeight: number
  comment?: string
}

export interface NewMedicineEvent {
  date: Date
  medicine: string
  comment: string
}

@Component({
    selector: 'app-provision',
    templateUrl: './provision.component.html',
    styleUrls: ['./provision.component.scss'],
    standalone: false
})
export class ProvisionComponent implements OnInit {
  constructor(private auth: Auth, private store: Firestore, private snack: MatSnackBar, private route: ActivatedRoute) {}

  dogId: string
  dogName: string

  foods: FoodEntryItem[]
  medicines: MedicineEntryItem[]

  selectedTabIdx = 0

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        map((params) => {
          this.dogId = params.get('dogId')
          this.dogName = params.get('dogName')
          return this.dogId
        })
      )
      .subscribe(() => {
        this.refreshFoods()
        this.refreshMedicine()
      })
  }

  private async refreshFoods() {
    const dogDoc = doc(this.store, 'dogs', this.dogId)
    const food = await getDocs(query(collection(dogDoc, FOOD_COLLECTION), orderBy('date', 'desc')))
    this.foods = food.docs.map((d: QueryDocumentSnapshot) => {
      const data = d.data() as FoodEntry
      return {
        docId: d.id,
        date: firebaseToDate(data.date),
        dogWeight: data.dogWeight,
        foodName: data.foodName,
        foodPortion: data.foodPortion,
        comment: data.comment,
        createdAt: firebaseToDate(data.createdAt),
        createdBy: data.createdBy,
      }
    })
  }

  private async refreshMedicine() {
    const dogDoc = doc(this.store, 'dogs', this.dogId)
    const food = await getDocs(query(collection(dogDoc, MEDICINE_COLLECTION), orderBy('date', 'desc')))
    this.medicines = food.docs.map((d: QueryDocumentSnapshot) => {
      const data = d.data() as MedicineEntry
      return {
        docId: d.id,
        date: firebaseToDate(data.date),
        medicine: data.medicine,
        comment: data.comment,
        createdAt: firebaseToDate(data.createdAt),
        createdBy: data.createdBy,
      }
    })
  }

  async newFoodEvent(event: NewFoodEvent) {
    console.log('food event', event)
    await this.saveEvent<FoodEntry>(
      {
        date: event.date,
        dogWeight: event.dogWeight,
        foodName: event.foodName,
        foodPortion: event.foodPortion,
        comment: event.comment || null,
      },
      FOOD_COLLECTION
    )
    this.refreshFoods()
  }

  async newMedicineEvent(event: NewMedicineEvent) {
    await this.saveEvent<MedicineEntry>(
      {
        date: event.date,
        medicine: event.medicine,
        comment: event.comment || null,
      },
      MEDICINE_COLLECTION
    )
    this.refreshMedicine()
  }

  async removeFood(docId: string) {
    await this.removeItem(docId, FOOD_COLLECTION)
    this.refreshFoods()
  }

  async removeMedicine(docId: string) {
    await this.removeItem(docId, MEDICINE_COLLECTION)
    this.refreshMedicine()
  }

  private async removeItem(docId: string, subCollection: string) {
    try {
      console.log('Remove item from collection', subCollection, docId)
      await deleteDoc(doc(this.store, 'dogs', this.dogId, subCollection, docId))
      this.snack.open('A bejegyzés sikeresen törölve', 'Ok', { duration: 2000 })
    } catch (e) {
      console.error(e)
      this.snack.open('Nem sikerült törölni a bejegyzést', 'Jaj most mi legyen?')
      throw e
    }
  }

  private async saveEvent<T>(data: Partial<T>, subCollection: string) {
    try {
      console.log('Saving for collection', subCollection, data)
      const dogDoc = doc(this.store, 'dogs', this.dogId)
      await addDoc(collection(dogDoc, subCollection), {
        ...data,
        createdAt: new Date(),
        createdBy: this.auth.currentUser.email,
      } as any)
      this.snack.open('Bejegyzés elmentve', 'Ok csá')
    } catch (e) {
      console.error(e)
      this.snack.open('Nem sikerült menteni a bejegyzést', 'Most mi tévő legyek?')
      throw e
    }
  }
}
